/**
 * @fileoverview Syndicator Showdown — Shared detailed leaderboard view
 * @module components/leaderboard/LeaderboardDetail
 *
 * The rich, single-game leaderboard experience — top scoreboard (top 20 +
 * a pinned "Your Position" row if the signed-in user is ranked outside the
 * top 20), and the signed-in user's own race-by-race "My Stable Progress"
 * breakdown. Used by BOTH:
 *   - /leaderboard/:slateId (LeaderboardPage.jsx)
 *   - /leaderboards, for whichever game is currently selected (LeaderboardsPage.jsx)
 * so both entry points render exactly the same data/markup for a given slate.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection, doc, query, orderBy, limit, onSnapshot, getDoc, getDocs,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { formatMinuteCountdown } from '../../utils/timezones';
import { getPublicName, getProfileImageUrl, DEFAULT_PROFILE_IMAGE } from '../../utils/profileImage';
import { hydratePublicProfiles } from '../../services/publicProfiles';
import ShareResultModal from '../share/ShareResultModal';
import './LeaderboardDetail.css';

// Top N ranked players shown before the "Your Position" pinned row cuts in.
const TOP_N = 20;

// ─── Helpers ────────────────────────────────────────────────────────────────

function toMs(v) {
  if (!v) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return new Date(v).getTime();
  if (typeof v?.toMillis === 'function') return v.toMillis();
  return null;
}

function fmt$(n) {
  if (!n && n !== 0) return '—';
  return '$' + Math.round(n).toLocaleString('en-AU');
}

function ordinal(n) {
  if (n === null || n === undefined) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function raceStatusLabel(race) {
  if (!race) return 'Upcoming';
  if (race.status === 'resulted') return 'Resulted';
  if (race.status === 'locked') return 'Live';
  return 'Upcoming';
}

function resolvePlacement(runnerName, officialPlacings) {
  if (!Array.isArray(officialPlacings) || !runnerName) return null;
  const idx = officialPlacings.findIndex(
    n => String(n).trim().toUpperCase() === String(runnerName).trim().toUpperCase()
  );
  return idx === -1 ? null : idx + 1;
}

function toRankingRow(d, idx) {
  return {
    id:                  d.id,
    userId:              d.data().userId || d.id,
    displayName:         d.data().displayName || d.data().userDisplayName || 'Anonymous',
    rank:                d.data().position || idx + 1,
    totalVirtualEarnings: d.data().totalEarnings || d.data().totalVirtualEarnings || 0,
    racesSettled:        d.data().racesSettled || 0,
    qualifiedForFinal:   d.data().qualifiedForFinal || false,
    stableSlug:          d.data().stableSlug || null,
  };
}

// ─── Leaderboard row ────────────────────────────────────────────────────────

function LeaderboardRow({ player, profile, onShare = null }) {
  const isPodium = player.rank <= 3;
  const podiumClass = player.rank === 1 ? 'gold' : player.rank === 2 ? 'silver' : 'bronze';

  // Public identity — stable name (username) over real displayName, hydrated
  // from the public player_profiles mirror where available, falling back to
  // the leaderboard row's own displayName snapshot.
  const publicName = getPublicName(
    { ...profile, displayName: profile?.displayName || player.displayName },
    null
  );
  const avatarUrl = getProfileImageUrl(profile, null);
  // Phase 7 — the public player_profiles mirror is the freshest source of
  // stableSlug (it's what claimStableSlug writes to); fall back to the
  // denormalised leaderboard row copy (see settleRaceResult.js) so a link
  // still works even before hydratePublicProfiles resolves.
  const stableSlug = profile?.stableSlug || player.stableSlug || null;

  return (
    <div className={`ss-leaderboard-row ${isPodium ? `ss-leaderboard-row--podium-${podiumClass}` : ''}`}>
      <div className="ss-rank-column font-data">
        <span className={`ss-rank-badge ${isPodium ? `ss-rank-badge--${podiumClass}` : ''}`}>
          {player.rank}
        </span>
      </div>

      <img
        className="ss-player-avatar"
        src={avatarUrl}
        alt=""
        onError={e => { e.currentTarget.src = DEFAULT_PROFILE_IMAGE; }}
      />

      <div className="ss-player-profile-column">
        {stableSlug ? (
          <Link to={`/stable/${stableSlug}`} className="ss-player-display-name ss-player-display-name--link">
            {publicName}
          </Link>
        ) : (
          <span className="ss-player-display-name">{publicName}</span>
        )}
        <div className="ss-player-meta-badges">
          <span className="ss-meta-pills">{player.racesSettled || 0} Legs Run</span>
          {player.qualifiedForFinal && (
            <span className="ss-meta-pills ss-meta-pills--qualified">🎟️ GOLDEN TICKET</span>
          )}
        </div>
      </div>

      <div className="ss-earnings-column font-data">
        <span className="ss-earnings-value">
          ${Math.round(player.totalVirtualEarnings || 0).toLocaleString('en-AU')}
        </span>
      </div>

      {onShare && (
        <button type="button" className="ss-row-share-btn" onClick={onShare} title="Share My Rank">
          🔗 Share
        </button>
      )}
    </div>
  );
}

// ─── My Stable Progress ─────────────────────────────────────────────────────
// Shows the logged-in user's own race-by-race breakdown for this Showdown,
// reusing the SAME race_allocations data settleRaceResult.js already wrote —
// never a second/independent payout calculation.

function MyStableProgress({ slateId, races, racesLoading, currentUser, highlightRaceId, gameName, ownUserDoc }) {
  const [entry, setEntry] = useState(undefined); // undefined = loading, null = no entry
  const [allocations, setAllocations] = useState({});
  const [rank, setRank] = useState(null);
  const [shareRace, setShareRace] = useState(null); // race object being shared, or null

  useEffect(() => {
    if (!currentUser || !slateId) { setEntry(null); return undefined; }
    let cancelled = false;
    setEntry(undefined);
    setAllocations({});
    (async () => {
      try {
        const entryId = `${slateId}_${currentUser.uid}`;
        const entrySnap = await getDoc(doc(db, 'player_entries', entryId));
        if (cancelled) return;
        if (!entrySnap.exists()) { setEntry(null); return; }
        setEntry({ id: entrySnap.id, ...entrySnap.data() });

        const allocSnap = await getDocs(collection(db, 'player_entries', entryId, 'race_allocations'));
        if (cancelled) return;
        const map = {};
        allocSnap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
        setAllocations(map);
      } catch (e) {
        console.error('[MyStableProgress] entry/allocation load error:', e.code, e.message, e);
        if (!cancelled) setEntry(null);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, slateId]);

  useEffect(() => {
    if (!currentUser || !slateId) { setRank(null); return undefined; }
    return onSnapshot(
      doc(db, 'game_slates', slateId, 'leaderboard', currentUser.uid),
      snap => setRank(snap.exists() ? (snap.data().position ?? null) : null),
      () => setRank(null)
    );
  }, [currentUser, slateId]);

  // Scroll/highlight the race named in ?race= once its row exists.
  useEffect(() => {
    if (!highlightRaceId || races.length === 0) return;
    const el = document.getElementById(`ss-progress-race-${highlightRaceId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightRaceId, races]);

  if (!currentUser) return null;

  if (racesLoading || entry === undefined) {
    return (
      <div className="ss-my-progress">
        <h2 className="ss-my-progress-title">My Stable Progress</h2>
        <div className="ss-my-progress-loading">
          <div className="ss-leaderboard-spinner" />
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="ss-my-progress">
        <h2 className="ss-my-progress-title">My Stable Progress</h2>
        <div className="ss-my-progress-empty">
          <p>You do not have a stable entered in this Showdown.</p>
          <Link to="/showdowns" className="ss-my-progress-cta">View Showdowns →</Link>
        </div>
      </div>
    );
  }

  const resultedRaces = races.filter(r => r.status === 'resulted');
  // There's no historical per-race rank snapshot stored anywhere — only the
  // current position — so it's only meaningful to attach it to the most
  // recently resulted race rather than fabricating it for earlier rows.
  const latestResultedRaceId = resultedRaces.length > 0
    ? resultedRaces[resultedRaces.length - 1].id
    : null;

  let runningTotal = 0;

  return (
    <div className="ss-my-progress">
      <h2 className="ss-my-progress-title">My Stable Progress</h2>

      <div className="ss-my-progress-list">
        {races.map(race => {
          const alloc      = allocations[race.id];
          const label      = raceStatusLabel(race);
          const isResulted = label === 'Resulted';
          const placings   = race.result?.officialPlacings || [];
          const runnerEntries = alloc?.runners ? Object.entries(alloc.runners) : [];
          const raceEarnings  = isResulted ? (alloc?.earnings || 0) : null;
          if (isResulted) runningTotal += (alloc?.earnings || 0);

          return (
            <div
              key={race.id}
              id={`ss-progress-race-${race.id}`}
              className={`ss-progress-race ${highlightRaceId === race.id ? 'ss-progress-race--highlight' : ''}`}
            >
              <div className="ss-progress-race-header">
                <span className="ss-progress-race-title">
                  Race {race.raceNumber ?? '?'}{race.raceName ? ` — ${race.raceName}` : ''}
                </span>
                <span className={`ss-progress-race-status ss-progress-race-status--${label.toLowerCase()}`}>
                  {label}
                </span>
              </div>

              {runnerEntries.length === 0 ? (
                <p className="ss-progress-race-empty">
                  {isResulted ? 'No runners selected in this race.' : 'Selections locked / awaiting result.'}
                </p>
              ) : (
                <div className="ss-progress-runner-table">
                  <div className="ss-progress-runner-row ss-progress-runner-row--head">
                    <span>Runner</span><span>Equity</span><span>Finish</span><span>Earned</span>
                  </div>
                  {runnerEntries.map(([runnerName, r]) => {
                    const position = isResulted ? resolvePlacement(runnerName, placings) : null;
                    const earned   = alloc?.earningsBreakdown?.[runnerName]?.dividend || 0;
                    return (
                      <div key={runnerName} className="ss-progress-runner-row">
                        <span className="ss-progress-runner-name">{runnerName}</span>
                        <span>{(r.equityPercent || 0).toFixed(0)}%</span>
                        <span>{isResulted ? (position ? `${position}${ordinal(position)}` : '—') : '—'}</span>
                        <span className={earned > 0 ? 'ss-progress-earned--pos' : ''}>
                          {isResulted ? fmt$(earned) : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="ss-progress-race-footer">
                <span>Race total: {isResulted ? fmt$(raceEarnings) : '—'}</span>
                {race.id === latestResultedRaceId && rank != null && (
                  <span>Leaderboard after Race {race.raceNumber}: {rank}{ordinal(rank)}</span>
                )}
                {isResulted && raceEarnings > 0 && (
                  <button
                    type="button"
                    className="ss-progress-share-btn"
                    onClick={() => {
                      let bestName = null;
                      let bestPos = Infinity;
                      runnerEntries.forEach(([name]) => {
                        const pos = resolvePlacement(name, placings);
                        if (pos != null && pos < bestPos) { bestPos = pos; bestName = name; }
                      });
                      setShareRace({
                        race,
                        runnerName: bestName,
                        finishingPosition: Number.isFinite(bestPos) ? bestPos : null,
                        virtualPayout: raceEarnings,
                      });
                    }}
                  >
                    🔗 Share Result
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {shareRace && entry && (
        <ShareResultModal
          isOpen={!!shareRace}
          onClose={() => setShareRace(null)}
          user={currentUser}
          userDoc={ownUserDoc}
          entry={entry}
          resultType={shareRace.finishingPosition === 1 ? 'winner' : 'top5'}
          runnerName={shareRace.runnerName}
          finishingPosition={shareRace.finishingPosition}
          raceNumber={shareRace.race.raceNumber}
          showdownName={gameName}
          virtualPayout={shareRace.virtualPayout}
        />
      )}

      {resultedRaces.length > 0 && (
        <div className="ss-my-progress-total">
          <span>Total Earned</span>
          <span className={runningTotal > 0 ? 'ss-progress-earned--pos' : ''}>{fmt$(runningTotal)}</span>
        </div>
      )}
    </div>
  );
}

// ─── LeaderboardDetail ──────────────────────────────────────────────────────

export const LeaderboardDetail = ({ slateId, highlightRaceId = null }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [rankings, setRankings]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [races, setRaces]             = useState([]);
  const [racesLoading, setRacesLoading] = useState(true);
  const [nowTick, setNowTick]         = useState(Date.now());
  const [profilesByUid, setProfilesByUid] = useState({});
  const [ownRow, setOwnRow]           = useState(null);
  const [gameName, setGameName]       = useState(null);
  const [ownUserDoc, setOwnUserDoc]   = useState(null);
  const [ownEntryForShare, setOwnEntryForShare] = useState(null);
  const [shareRankOpen, setShareRankOpen] = useState(false);

  useEffect(() => onAuthStateChanged(auth, setCurrentUser), []);

  // Showdown name — used only for share-result/rank copy, so a failure here
  // is non-fatal (the share card just omits the showdown name).
  useEffect(() => {
    if (!slateId) return;
    getDoc(doc(db, 'game_slates', slateId))
      .then(snap => { if (snap.exists()) setGameName(snap.data().gameName || null); })
      .catch(() => {});
  }, [slateId]);

  // The signed-in player's own private users/{uid} doc — needed for
  // getPublicName()/getProfileImageUrl() on Share Result/Rank cards (the
  // public player_profiles mirror only covers OTHER players already shown
  // on the board, not necessarily the viewer themselves).
  useEffect(() => {
    if (!currentUser) { setOwnUserDoc(null); return undefined; }
    let cancelled = false;
    getDoc(doc(db, 'users', currentUser.uid))
      .then(snap => { if (!cancelled) setOwnUserDoc(snap.exists() ? snap.data() : null); })
      .catch(() => { if (!cancelled) setOwnUserDoc(null); });
    return () => { cancelled = true; };
  }, [currentUser]);

  // Own entry doc — only needed to back a "Share My Rank" public link
  // (getOrCreateStableShareForEntry). player_entries doc IDs are
  // deterministic (`${slateId}_${uid}`), same convention as MyStableProgress.
  useEffect(() => {
    if (!currentUser || !slateId) { setOwnEntryForShare(null); return undefined; }
    let cancelled = false;
    getDoc(doc(db, 'player_entries', `${slateId}_${currentUser.uid}`))
      .then(snap => { if (!cancelled) setOwnEntryForShare(snap.exists() ? { id: snap.id, ...snap.data() } : null); })
      .catch(() => { if (!cancelled) setOwnEntryForShare(null); });
    return () => { cancelled = true; };
  }, [currentUser, slateId]);

  // Countdown/empty-state tick — every 30s is plenty for a minute-granularity display.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!slateId) return;
    setLoading(true);

    // Top N only — the pinned "Your Position" row (below) covers anyone
    // outside this range with a single extra doc read instead of an
    // unbounded fetch.
    const q = query(
      collection(db, 'game_slates', slateId, 'leaderboard'),
      orderBy('position', 'asc'),
      limit(TOP_N)
    );

    const unsubscribe = onSnapshot(q, snap => {
      setRankings(snap.docs.map(toRankingRow));
      setLoading(false);
    }, err => {
      console.error('[LeaderboardDetail] onSnapshot error:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [slateId]);

  // Pin the current user's row even if they're ranked outside the top N —
  // the leaderboard doc id is the userId, so this is a cheap single-doc
  // listener. Never duplicated: if the user is already in `rankings`
  // (rank 1..N) this is cleared instead of rendered a second time.
  useEffect(() => {
    if (!currentUser || !slateId) { setOwnRow(null); return undefined; }
    return onSnapshot(
      doc(db, 'game_slates', slateId, 'leaderboard', currentUser.uid),
      snap => setOwnRow(snap.exists() ? toRankingRow(snap, 0) : null),
      () => setOwnRow(null)
    );
  }, [currentUser, slateId]);

  const isOwnRowPinned = !!(ownRow && !rankings.some(r => r.userId === ownRow.userId));

  // Public identity hydration — leaderboard rows only carry a displayName
  // snapshot from settlement time, so fetch the public player_profiles/{uid}
  // mirror (stable name + avatar) for every player currently on the board.
  // player_profiles is publicly readable; users/{uid} (private) is not.
  useEffect(() => {
    const uids = rankings.map(r => r.userId);
    if (isOwnRowPinned) uids.push(ownRow.userId);
    const missing = [...new Set(uids)].filter(uid => uid && !(uid in profilesByUid));
    if (missing.length === 0) return;
    let cancelled = false;
    hydratePublicProfiles(missing).then(map => {
      if (cancelled) return;
      setProfilesByUid(prev => ({ ...prev, ...map }));
    });
    return () => { cancelled = true; };
  }, [rankings, isOwnRowPinned, ownRow, profilesByUid]);

  // Race timing/status — powers "Board opens after Race 1" empty state and
  // the My Stable Progress race-by-race breakdown below.
  useEffect(() => {
    if (!slateId) return;
    let cancelled = false;
    setRacesLoading(true);
    getDocs(query(collection(db, 'game_slates', slateId, 'races'), orderBy('raceNumber', 'asc')))
      .then(snap => {
        if (cancelled) return;
        setRaces(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      })
      .catch(e => console.error('[LeaderboardDetail] races load error:', e.code, e.message, e))
      .finally(() => { if (!cancelled) setRacesLoading(false); });
    return () => { cancelled = true; };
  }, [slateId]);

  const raceOneInfo = useMemo(() => {
    if (races.length === 0) return null;
    const race1 = races.find(r => Number(r.raceNumber) === 1) || races[0];
    return {
      resulted: race1.status === 'resulted',
      startMs:  toMs(race1.startTimestamp ?? race1.scheduledJump),
    };
  }, [races]);

  const hasAnyResult = rankings.length > 0 || isOwnRowPinned;

  if (loading) {
    return (
      <div className="ss-leaderboard-container">
        <div className="ss-leaderboard-center">
          <div className="ss-leaderboard-spinner" />
          <p className="ss-leaderboard-loading-text">Streaming live rankings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ss-leaderboard-container">
      {/* Scoreboard Dashboard Header */}
      <div className="ss-leaderboard-header">
        <div className="ss-header-branding">
          <span className="ss-trophy-icon">🏆</span>
          <div>
            <h1 className="ss-leaderboard-title font-data">LIVE SCOREBOARD</h1>
            <p className="ss-leaderboard-subtitle">Real-time carnival tracking • Top {TOP_N} stables</p>
          </div>
        </div>
      </div>

      {/* Rankings List Container */}
      <div className="ss-leaderboard-list">
        {rankings.length === 0 ? (
          <div className="ss-leaderboard-empty">
            {!racesLoading && raceOneInfo && !raceOneInfo.resulted ? (
              <>
                <p className="ss-leaderboard-empty-title">Leaderboard opens after Race 1</p>
                <p className="ss-leaderboard-empty-sub">
                  Rankings will appear once the first race has resulted.
                </p>
                {raceOneInfo.startMs && raceOneInfo.startMs > nowTick && (
                  <p className="ss-leaderboard-empty-countdown">
                    Race 1 starts in {formatMinuteCountdown(raceOneInfo.startMs - nowTick)}
                  </p>
                )}
                <div className="ss-leaderboard-empty-actions">
                  <Link to="/showdowns" className="ss-leaderboard-empty-btn">Back to Showdowns</Link>
                  <Link to="/my-stable" className="ss-leaderboard-empty-btn ss-leaderboard-empty-btn--outline">My Stable</Link>
                </div>
              </>
            ) : (
              <p>No stables active on this slate yet. Rankings activate as legs jump.</p>
            )}
          </div>
        ) : (
          <>
            {rankings.map(player => (
              <LeaderboardRow
                key={player.userId}
                player={player}
                profile={profilesByUid[player.userId]}
                onShare={(currentUser?.uid === player.userId && ownEntryForShare) ? () => setShareRankOpen(true) : null}
              />
            ))}
            {isOwnRowPinned && (
              <>
                <div className="ss-leaderboard-divider">YOUR POSITION</div>
                <LeaderboardRow
                  player={ownRow}
                  profile={profilesByUid[ownRow.userId]}
                  onShare={ownEntryForShare ? () => setShareRankOpen(true) : null}
                />
              </>
            )}
          </>
        )}
      </div>

      {ownEntryForShare && (isOwnRowPinned || rankings.some(r => r.userId === currentUser?.uid)) && (
        <ShareResultModal
          isOpen={shareRankOpen}
          onClose={() => setShareRankOpen(false)}
          user={currentUser}
          userDoc={ownUserDoc}
          entry={ownEntryForShare}
          resultType="leaderboard"
          rank={(isOwnRowPinned ? ownRow : rankings.find(r => r.userId === currentUser?.uid))?.rank}
          winnings={(isOwnRowPinned ? ownRow : rankings.find(r => r.userId === currentUser?.uid))?.totalVirtualEarnings}
          showdownName={gameName}
        />
      )}

      {/* My Stable Progress — logged-in user's own race-by-race breakdown */}
      {(hasAnyResult || !racesLoading) && (
        <MyStableProgress
          slateId={slateId}
          races={races}
          racesLoading={racesLoading}
          currentUser={currentUser}
          highlightRaceId={highlightRaceId}
          gameName={gameName}
          ownUserDoc={ownUserDoc}
        />
      )}
    </div>
  );
};

export default LeaderboardDetail;
